/**
 * What the filing's own text says about an imbalance.
 *
 * STEP TWO of the import flow. Step one imports what was read even where the balance sheet
 * does not balance, because refusing left the user with nothing to correct. This reads the
 * filing a second time — from the transcription the import stored on the model — and says
 * which figure is probably wrong.
 *
 * IT CORRECTS ONLY WHAT THE FILING DISAGREES WITH. This began read-only, and one button was
 * added deliberately: where the filing prints a different figure on a row about that line,
 * taking the filing's figure is correcting a misreading, and the source is the page rather
 * than arithmetic.
 *
 * What it still refuses is the other thing — moving a figure to make the sheet balance.
 * Something can always be found that closes a gap of 17,400,000, and a figure moved for that
 * reason is wrong twice over: once as a figure, and once as a claim that the accounts add up.
 * So nothing is nudged, only replaced by what the page says, and the result reports the balance
 * check before and after rather than announcing success. Correcting a misread balance changes
 * that balance and the cash beside it; a gap from elsewhere survives it, and it says so.
 *
 * The assumption-nudging fix lives next door under the diagnosis, where it belongs and where
 * its own docstring calls it what it is.
 *
 * Ordered by what each candidate proves. An exact explanation comes first and says so; a
 * loose one shows what it leaves unexplained; the weakest kind — a figure the filing prints
 * nowhere — is last and says why it proves little, since v2 derives cash and several other
 * balances and a derived figure SHOULD be absent from the filing.
 */

import { useEffect } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { BalanceTextCandidate } from '../../../types/financial-model-v2';

function money(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const KIND_LABEL: Record<BalanceTextCandidate['kind'], string> = {
  printed_differently: 'The filing prints a different figure',
  wrong_side: 'A line may be on the wrong side',
  counted_twice_or_omitted: 'A line may be counted twice, or left out',
  not_printed: 'Not printed in the filing',
};

const KIND_TONE: Record<BalanceTextCandidate['kind'], string> = {
  printed_differently: 'border-red-300 bg-red-50',
  wrong_side: 'border-amber-300 bg-amber-50',
  counted_twice_or_omitted: 'border-amber-300 bg-amber-50',
  not_printed: 'border-gray-200 bg-gray-50',
};

function CandidateCard({ candidate }: { candidate: BalanceTextCandidate }) {
  const exact = Math.abs(candidate.residual) < 1;
  return (
    <div className={`rounded border px-4 py-3 text-sm ${KIND_TONE[candidate.kind]}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold text-gray-900">
          {KIND_LABEL[candidate.kind]} — {candidate.label}
        </span>
        <span className="shrink-0 text-xs text-gray-600">{candidate.period}</span>
      </div>

      {candidate.printedValue !== null && (
        <div className="mt-2 flex gap-6 text-sm">
          <span>
            <span className="text-gray-500">the model holds</span>{' '}
            <span className="font-mono">{money(candidate.modelValue)}</span>
          </span>
          <span>
            <span className="text-gray-500">the filing prints</span>{' '}
            <span className="font-mono font-semibold">{money(candidate.printedValue)}</span>
          </span>
        </div>
      )}

      <p className="mt-2 text-gray-700">{candidate.because}</p>

      {/* The row it came from. A proposal a reader cannot check against the page is a
          proposal taken on trust, which is what this whole step exists to avoid. */}
      {candidate.evidence && (
        <div className="mt-2 rounded bg-white/70 px-3 py-2 text-xs text-gray-600">
          from <span className="font-medium">{candidate.evidence.statement}</span>, row{' '}
          <span className="font-medium">
            {candidate.evidence.caption || '(no caption)'}
          </span>
          {candidate.evidence.isTotal && ' — a printed subtotal'}
        </div>
      )}

      <div className="mt-2 text-xs text-gray-500">
        {exact
          ? 'Accounts for the imbalance exactly.'
          : `Leaves ${money(candidate.residual)} of the imbalance unexplained.`}
      </div>
    </div>
  );
}

export function BalanceFromTextPanel() {
  const model = useFinancialModelV2Store((s) => s.model);
  const proposal = useFinancialModelV2Store((s) => s.balanceFromText);
  const readBalanceFromText = useFinancialModelV2Store((s) => s.readBalanceFromText);
  const applyBalanceFromText = useFinancialModelV2Store((s) => s.applyBalanceFromText);
  const applied = useFinancialModelV2Store((s) => s.balanceTextApplied);

  // Re-read whenever the model changes: a proposal about figures the user has since
  // corrected is worse than none.
  useEffect(() => {
    void readBalanceFromText();
  }, [readBalanceFromText, model?.version]);

  if (!model || !proposal) return null;
  if (Object.keys(proposal.imbalance).length === 0) return null;

  const strong = proposal.candidates.filter((c) => c.kind !== 'not_printed');
  const weak = proposal.candidates.filter((c) => c.kind === 'not_printed');

  // Only an EXACT explanation that has somewhere to be written. A loose fit is shown and left
  // to the user; a figure the model derives has no input to write into at all.
  const correctable = strong.filter(
    (c) =>
      c.kind === 'printed_differently' && c.appliesTo !== '' && Math.abs(c.residual) < 1,
  );

  return (
    <div className="space-y-3">
      <div className="rounded border border-gray-200 bg-white px-4 py-3">
        <div className="text-sm font-semibold text-gray-900">
          Checked against the filing's own text
        </div>
        <p className="mt-1 text-sm text-gray-600">
          The filing read a second time, structurally, to find which figure the imbalance
          points at. Where the page prints a different figure, the page is the source and
          taking its figure corrects a misreading. Nothing is nudged to force the sheet to
          balance — anything below without a figure from the page is for you to judge.
        </p>

        {correctable.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => void applyBalanceFromText()}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
            >
              Take the filing&rsquo;s {correctable.length} figure
              {correctable.length === 1 ? '' : 's'}
            </button>
            <span className="ml-2 text-xs text-gray-500">
              {correctable.map((c) => `${c.label} (${c.period})`).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* What it changed, and whether it was enough. Reported rather than celebrated: the
          common outcome is a corrected figure and a gap that came from somewhere else. */}
      {applied && (
        <div
          className={`rounded border px-4 py-3 text-sm ${
            applied.balances
              ? 'border-green-300 bg-green-50 text-green-900'
              : 'border-amber-300 bg-amber-50 text-amber-900'
          }`}
        >
          <div className="font-semibold">
            {applied.corrections.length === 0
              ? 'Nothing was changed'
              : `${applied.corrections.length} figure${
                  applied.corrections.length === 1 ? '' : 's'
                } taken from the filing`}
            {applied.corrections.length > 0 &&
              (applied.balances
                ? ' — the accounts now add up.'
                : ' — the accounts still do not add up.')}
          </div>
          <ul className="mt-2 space-y-1">
            {applied.corrections.map((correction) => (
              <li key={`${correction.field}-${correction.period}`} className="font-mono text-xs">
                {correction.label} {correction.period}: {money(correction.was)} →{' '}
                {money(correction.now)}
              </li>
            ))}
          </ul>
          {applied.notes.map((note) => (
            <p key={note} className="mt-2">
              {note}
            </p>
          ))}
        </div>
      )}

      {proposal.notes.map((note) => (
        <div
          key={note}
          className="rounded border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700"
        >
          {note}
        </div>
      ))}

      {strong.map((candidate, index) => (
        <CandidateCard key={`${candidate.kind}-${candidate.field}-${index}`} candidate={candidate} />
      ))}

      {proposal.hasText && strong.length === 0 && (
        <div className="rounded border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
          Nothing in the filing's text accounts for the imbalance. No figure it prints
          differs from the model by the amount the sheet is out by, so the misreading is
          probably in a figure the filing does not print on the face of a statement — or in
          how two of them were combined.
        </div>
      )}

      {/* The other half of the answer: where the error is NOT. */}
      {proposal.confirmed.length > 0 && (
        <div className="rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900">
          <span className="font-semibold">
            {proposal.confirmed.length} line
            {proposal.confirmed.length === 1 ? '' : 's'} the filing confirms:
          </span>{' '}
          {proposal.confirmed.join(', ')}. The error is not in these.
        </div>
      )}

      {weak.length > 0 && (
        <details className="rounded border border-gray-200 bg-white px-4 py-2 text-sm">
          <summary className="cursor-pointer text-gray-700">
            {weak.length} figure{weak.length === 1 ? '' : 's'} the filing prints nowhere
          </summary>
          <div className="mt-2 space-y-2">
            {weak.map((candidate, index) => (
              <CandidateCard key={`weak-${candidate.field}-${index}`} candidate={candidate} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
